import './otp.scss';

import isEmpty from 'lodash/isEmpty';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useRef,
} from 'react';

import { NAVIGATION_KEYS } from './constants/navigationKeys';
import { DEFAULT_OTP_LENGTH } from './constants/otpLength';
import { OTPProps } from './types/OTPProps';

export const OTP = ({
  autoFocus = true,
  disabled = false,
  error,
  errorTestId,
  inputTestId,
  length = DEFAULT_OTP_LENGTH,
  onChange,
  value,
}: OTPProps): ReactNode => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Set right before we auto-focus the next box, so that focus event
  // skips the skip-ahead guard below (its `focusIndex` is still stale
  // since `value` hasn't updated yet).
  const skipFocusGuard = useRef(false);
  const previousValueRef = useRef(value);

  const hasError = !isEmpty(error);

  const digits = Array.from(
    { length },
    (_, index): { digit: string; name: string } => ({
      digit: value[index] ?? '',
      name: `otp-${index}`,
    }),
  );
  const firstEmptyIndex = digits.findIndex(({ digit }): boolean => !digit);
  const focusIndex = firstEmptyIndex === -1 ? length - 1 : firstEmptyIndex;

  useEffect((): void => {
    // Only autofocus once, on mount.
    if (autoFocus) {
      inputRefs.current[focusIndex]?.focus();
    }
  }, []);

  useEffect((): void => {
    // If `value` is cleared externally after being non-empty, refocus the
    // first box so the user can start typing the new code immediately,
    // without needing to click back in.
    if (autoFocus && previousValueRef.current && !value) {
      inputRefs.current[0]?.focus();
    }

    previousValueRef.current = value;
  }, [value]);

  const focusInput = (index: number): void => {
    inputRefs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const updateDigits = (mutate: (nextDigits: string[]) => void): void => {
    const nextDigits = digits.map(({ digit }): string => digit);

    mutate(nextDigits);

    onChange(nextDigits.join(''));
  };

  const handleChange =
    (index: number) =>
    (event: ChangeEvent<HTMLInputElement>): void => {
      const characters = event.target.value.replace(/\D/g, '');

      if (!characters) {
        updateDigits((nextDigits): void => {
          nextDigits[index] = '';
        });
        return;
      }

      // A single change event can carry more than one character here - either
      // from a manual paste or a mobile OS filling the whole code in from an
      // SMS. Either way, spread the characters across this box and the ones
      // after it.
      let nextIndex = index;

      updateDigits((nextDigits): void => {
        for (const character of characters) {
          if (nextIndex >= length) {
            break;
          }

          nextDigits[nextIndex] = character;
          nextIndex += 1;
        }
      });

      skipFocusGuard.current = true;
      focusInput(nextIndex);
    };

  const handleKeyDown =
    (index: number) =>
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        focusInput(index - 1);
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        focusInput(index + 1);
        return;
      }

      if (event.key === 'Backspace' && !digits[index].digit) {
        event.preventDefault();
        updateDigits((nextDigits): void => {
          nextDigits[index - 1] = '';
        });
        focusInput(index - 1);
        return;
      }

      if (
        !NAVIGATION_KEYS.includes(event.key) &&
        !event.ctrlKey &&
        !event.metaKey &&
        (isNaN(Number(event.key)) || event.key === ' ')
      ) {
        event.preventDefault();
      }
    };

  const handleFocus =
    (index: number) =>
    (event: FocusEvent<HTMLInputElement>): void => {
      event.target.select();

      if (skipFocusGuard.current) {
        skipFocusGuard.current = false;
        return;
      }

      // Users can revisit a filled box to correct it, but can't jump ahead of
      // the next empty one.
      if (index > focusIndex) {
        focusInput(focusIndex);
      }
    };

  return (
    <div className="otp">
      <div className="otp-inputs">
        {digits.map(
          ({ digit, name }, index): ReactNode => (
            <input
              aria-invalid={hasError}
              aria-label={`Digit ${index + 1} of ${length}`}
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              className={`otp-digit ${hasError ? 'otp-digit-error' : ''}`}
              data-testid={inputTestId?.(index + 1)}
              disabled={disabled}
              inputMode="numeric"
              key={name}
              maxLength={length}
              name={name}
              pattern="[0-9]*"
              ref={(element: HTMLInputElement | null): void => {
                inputRefs.current[index] = element;
              }}
              type="text"
              value={digit}
              onChange={handleChange(index)}
              onFocus={handleFocus(index)}
              onKeyDown={handleKeyDown(index)}
            />
          ),
        )}
      </div>

      {hasError && (
        <span className="otp-error" data-testid={errorTestId}>
          {error}
        </span>
      )}
    </div>
  );
};
